import Vue from 'vue'
import { $_bridge } from '@/nms/plugins/bridge.js'
import uid from '@/nms/components/utils/uid'

// Типы
interface DownloadItem {
  id: string
  queue_id: string
  data?: any
  params?: any
  callback?: {
    pending?: (item: any) => void
    progress?: (item: any) => void
    success?: (item: any, response: any) => void
    error?: (item: any, error: any) => void
  }
  total: {
    bytes: number
  }
  downloaded: {
    bytes: number
    percents: number
  }
}

interface DownloadQueue {
  id: string
  total: {
    bytes: number
    items: number
  }
  downloaded: {
    bytes: number
    items: number
  }
  list: Record<string, DownloadItem>
  abortControllers?: Record<string, AbortController>
}

interface DownloadState {
  queue: Record<string, DownloadQueue>
}

// Начальное состояние
const getDefaultState = (): DownloadState => ({
  queue: {}
})

// Состояние
const state = getDefaultState()

// Мутации
const mutations = {
  SET_QUEUE_DATA(state: DownloadState, queue: DownloadQueue) {
    // Используем Vue.set для реактивности
    Vue.set(state.queue, queue.id, queue)
  },

  SET_QUEUE_ITEM_TOTAL(state: DownloadState, { item, total }: { item: DownloadItem; total: { bytes: number } }) {
    const queue = state.queue[item.queue_id]
    if (queue?.list[item.id]) {
      Vue.set(queue.list[item.id], 'total', total)
    }
  },

  UPDATE_QUEUE_ITEM_DOWNLOADED(state: DownloadState, { item, downloaded }: { item: DownloadItem; downloaded: { bytes: number; percents: number } }) {
    const queue = state.queue[item.queue_id]
    if (queue?.list[item.id]) {
      Vue.set(queue.list[item.id], 'downloaded', downloaded)
    }
  },

  INCREMENT_QUEUE_DOWNLOADED_ITEMS(state: DownloadState, queue: DownloadQueue) {
    if (state.queue[queue.id]) {
      Vue.set(state.queue[queue.id].downloaded, 'items', state.queue[queue.id].downloaded.items + 1)
    }
  },

  UPDATE_QUEUE_TOTAL_BYTES(state: DownloadState, { queueId, bytes }: { queueId: string; bytes: number }) {
    if (state.queue[queueId]) {
      const newTotal = state.queue[queueId].total.bytes + bytes
      Vue.set(state.queue[queueId].total, 'bytes', newTotal)
    }
  },

  CLEAR_QUEUE_DATA(state: DownloadState, { queue_id }: { queue_id: string }) {
    Vue.delete(state.queue, queue_id)
  },

  // RESET_ALL_QUEUES(state: DownloadState) {
  //   state.queue = {}
  // }
}

// Геттеры
const getters = {
  queues: (state: DownloadState) => state.queue,
  
  queue: (state: DownloadState) => (queueId: string) => state.queue[queueId],
  
  queueItems: (state: DownloadState) => (queueId: string) => {
    const queue = state.queue[queueId]
    return queue ? Object.values(queue.list) : []
  },
  
  queueProgress: (state: DownloadState) => (queueId: string) => {
    const queue = state.queue[queueId]
    if (!queue || queue.total.bytes === 0) return 0
    return Math.round((queue.downloaded.bytes * 100) / queue.total.bytes)
  }
}

// Вспомогательные функции для уведомлений
const notifications = {
  pending: (item: DownloadItem) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.data?.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate">Подготовка к скачиванию…</span>
    </div>
    <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-linear mb-0" style="height: 4px;">
      <div class="v-progress-linear__background primary" style="height: 4px; opacity: 0.3; width: 100%;"></div>
      <div class="v-progress-linear__bar">
        <div class="v-progress-linear__bar__indeterminate v-progress-linear__bar__indeterminate--active">
          <div class="v-progress-linear__bar__indeterminate long primary"></div>
          <div class="v-progress-linear__bar__indeterminate short primary"></div>
        </div>
      </div>
    </div>`
  },

  progress: (item: DownloadItem, progress: number) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.data?.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate">Файл скачан на ${progress}%</span>
    </div>
    <div role="progressbar" aria-valuemin="0" aria-valuemax="100" class="v-progress-linear mb-0" style="height: 4px;">
      <div class="v-progress-linear__background primary" style="height: 4px; opacity: 0.3; width: 100%;"></div>
      <div class="v-progress-linear__bar">
        <div class="v-progress-linear__bar__determinate accent" style="width: ${progress}%;"></div>
      </div>
    </div>`
  },

  success: (item: DownloadItem) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.data?.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate success--text">Файл успешно скачан!</span>
    </div>`
  },

  error: (item: DownloadItem, errorMsg: string) => {
    return `<div class="fit text-xs-left">
      <span class="text-truncate block">${item.data?.name || 'Файл'}</span>
    </div>
    <div class="caption fit text-xs-left">
      <span class="text-truncate error--text">Ошибка: ${errorMsg}</span>
    </div>`
  }
}

// Действия
const actions = {
  /**
   * Execute download for multiple files
   */
  async execute({ commit, state }: any, [list, vm]: [any[], any]) {
    return new Promise(async (resolve, reject) => {
      const queueId = uid('queue')
      const abortControllers: Record<string, AbortController> = {}
      
      try {
        // 1. Создаём карту файлов
        const items: Record<string, DownloadItem> = {}
        let totalBytes = 0
        
        for (const item of list) {
          const fileId = uid('file')
          items[fileId] = {
            id: fileId,
            queue_id: queueId,
            total: { bytes: 0 },
            downloaded: { bytes: 0, percents: 0 },
            ...item
          }
        }
        
        // 2. Устанавливаем очередь
        const queue: DownloadQueue = {
          id: queueId,
          total: { bytes: 0, items: list.length },
          downloaded: { bytes: 0, items: 0 },
          list: items,
          abortControllers
        }
        
        commit('SET_QUEUE_DATA', queue)
        
        // 3. Запускаем загрузку каждого файла
        const downloadPromises = Object.keys(items).map(async (fileId) => {
          const item = items[fileId]
          const abortController = new AbortController()
          abortControllers[fileId] = abortController
          
          // Вызываем колбэк pending
          if (item.callback?.pending) {
            item.callback.pending(item)
          } else {
            this.dispatch('file/download/notifyPending', { item, vm }, { root: true })
          }
          
          try {
            // Настройка запроса
            const response = await vm.$http({
              method: item.params?.method || 'GET',
              url: item.params?.url,
              responseType: item.params?.responseType || 'blob',
              params: item.data,
              signal: abortController.signal,
              onDownloadProgress: (progressEvent: any) => {
                const totalLength = progressEvent.lengthComputable
                  ? progressEvent.total
                  : progressEvent.target?.getResponseHeader('content-length')
                    || progressEvent.target?.getResponseHeader('x-decompressed-content-length')
                
                if (totalLength) {
                  const progress = Math.round((progressEvent.loaded * 100) / totalLength)
                  
                  // Обновляем состояние
                  commit('SET_QUEUE_ITEM_TOTAL', {
                    item,
                    total: { bytes: totalLength }
                  })
                  
                  commit('UPDATE_QUEUE_ITEM_DOWNLOADED', {
                    item,
                    downloaded: {
                      bytes: progressEvent.loaded,
                      percents: progress
                    }
                  })
                  
                  // Обновляем общие байты очереди
                  commit('UPDATE_QUEUE_TOTAL_BYTES', {
                    queueId,
                    bytes: progressEvent.loaded - (item.downloaded?.bytes || 0)
                  })
                  
                  // Вызываем колбэк прогресса
                  if (item.callback?.progress) {
                    item.callback.progress(item)
                  } else {
                    this.dispatch('file/download/notifyProgress', { item, progress, vm }, { root: true })
                  }
                }
              }
            })
            
            // Успешная загрузка
            commit('INCREMENT_QUEUE_DOWNLOADED_ITEMS', queue)
            
            // Добавляем статистику очереди в ответ
            const updatedQueue = state.queue[queueId]
            response.total = updatedQueue?.total
            response.downloaded = updatedQueue?.downloaded
            
            if (item.callback?.success) {
              item.callback.success(item, response)
            } else {
              this.dispatch('file/download/notifySuccess', { item, response, vm }, { root: true })
            }
            
            return { success: true, item, response }
            
          } catch (error: any) {
            if (error.name === 'AbortError') {
              console.log(`Download cancelled for ${item.data?.name}`)
            } else {
              console.error(`Download failed for ${item.data?.name}:`, error)
              
              if (item.callback?.error) {
                item.callback.error(item, error)
              } else {
                this.dispatch('file/download/notifyError', { item, error, vm }, { root: true })
              }
            }
            return { success: false, item, error }
          }
        })
        
        // Ждём завершения всех загрузок
        const results = await Promise.all(downloadPromises)
        resolve(results)
        
      } catch (error) {
        console.error('Queue execution failed:', error)
        reject(error)
      }
    })
  },
  
  /**
   * Cancel specific download in queue
   */
  cancelDownload({ commit, state }: any, { queueId, fileId }: { queueId: string; fileId: string }) {
    const queue = state.queue[queueId]
    if (queue?.abortControllers?.[fileId]) {
      queue.abortControllers[fileId].abort()
      delete queue.abortControllers[fileId]
    }
  },
  
  /**
   * Cancel all downloads in queue
   */
  cancelAllDownloads({ commit, state }: any, queueId: string) {
    const queue = state.queue[queueId]
    if (queue?.abortControllers) {
      Object.values(queue.abortControllers).forEach(controller => {
        controller.abort()
      })
      queue.abortControllers = {}
    }
  },
  
  /**
   * Clear queue data
   */
  clearQueue({ commit }: any, queueId: string) {
    commit('CLEAR_QUEUE_DATA', { queue_id: queueId })
  },
  
  /**
   * Open PDF in browser window
   */
  open({ commit, state }: any, { blob, mimetype }: { blob: Blob; mimetype?: string }) {
    const blobWithType = new Blob([blob], { type: mimetype || 'application/pdf' })
    
    // IE/Edge fallback
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blobWithType)
      return
    }
    
    const data = window.URL.createObjectURL(blobWithType)
    const newWindow = window.open(data)
    if (newWindow) {
      newWindow.focus()
    }
  },
  
  /**
   * Save file using File System Access API (modern browsers)
   */
  async streamSave({ commit, state }: any, { filename, vm }: { filename: string; vm: any }) {
    try {
      console.log('Отправка запроса к серверу...')
      const response = await $_bridge.downloadBackup(filename)
      
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}: ${response.statusText}`)
      }
      
      if (!response.body) {
        throw new Error('Ответ не содержит тела в виде потока (ReadableStream)')
      }
      
      // Получаем размер файла
      const contentLength = response.headers.get('x-file-size')
      let sizeInfo = ''
      if (contentLength) {
        const bytes = parseInt(contentLength, 10)
        const mb = (bytes / (1024 * 1024)).toFixed(2)
        sizeInfo = ` (${mb} МБ)`
      }
      
      // Проверяем поддержку File System Access API
      if ('showSaveFilePicker' in window && !(window as any).__PLAYWRIGHT__) {
        try {
          const fileHandle = await (window as any).showSaveFilePicker({ 
            suggestedName: filename 
          })
          
          vm.$dialog.notify.success(
            `Размер файла${sizeInfo}. Начинаю загрузку...`,
            { timeout: 3000 }
          )
          
          const writable = await fileHandle.createWritable()
          await response.body.pipeTo(writable)
          
          vm.$dialog.notify.success(
            `Файл ${fileHandle.name} успешно загружен.`,
            { timeout: 3000 }
          )
          console.log('✅ Файл успешно сохранён')
          
        } catch (err: any) {
          if (err.name === 'AbortError') {
            console.log('Сохранение отменено пользователем')
          } else {
            throw err
          }
        }
      } else {
        // Fallback для неподдерживаемых браузеров
        console.log('Используется fallback для сохранения файла')
        
        const blob = await response.blob()
        
        if (!contentLength) {
          const mb = (blob.size / (1024 * 1024)).toFixed(2)
          sizeInfo = ` (${mb} МБ)`
        }
        
        vm.$dialog.notify.success(
          `Размер файла${sizeInfo}. Начинаю загрузку...`,
          { timeout: 3000 }
        )
        
        const url = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = filename
        link.click()
        URL.revokeObjectURL(url)
        
        vm.$dialog.notify.success(`Файл успешно загружен.`, { timeout: 3000 })
        console.log('✅ Файл успешно скачан через fallback')
      }
      
    } catch (error) {
      console.error('Необработанная ошибка в streamSave:', error)
      vm.$dialog.notify.error('Не удалось загрузить файл', { timeout: 3000 })
    }
  },
  
  /**
   * Save file (legacy method)
   */
  save({ commit, state }: any, { content, filename, mimetype }: { content: any; filename: string; mimetype?: string }) {
    const fileAsBlob = new Blob([content], { type: mimetype || 'application/octet-stream' })
    const downloadLink = document.createElement('a')
    downloadLink.download = filename
    downloadLink.href = window.URL.createObjectURL(fileAsBlob)
    downloadLink.style.display = 'none'
    document.body.appendChild(downloadLink)
    downloadLink.click()
    document.body.removeChild(downloadLink)
    window.URL.revokeObjectURL(downloadLink.href)
  },
  
  // Уведомления
  notifyPending({ commit, state }: any, { item, vm }: { item: DownloadItem; vm: any }) {
    if (vm && vm.$snotify) {
      vm.$snotify.html(notifications.pending(item), {
        id: `toast-${item.id}`,
        timeout: 0,
        closeOnClick: true,
        pauseOnHover: false
      })
    } else {
      console.warn('Default pending callback for:', item.data?.name)
    }
  },
  
  notifyProgress({ commit, state }: any, { item, progress, vm }: { item: DownloadItem; progress: number; vm: any }) {
    const toastId = `toast-${item.id}`
    const toast = vm?.$snotify?.get(toastId)
    
    if (toast && vm?.$snotify) {
      vm.$snotify.update(toastId, notifications.progress(item, progress), {
        timeout: 0,
        closeOnClick: true
      })
    } else if (vm && vm.$snotify) {
      vm.$snotify.html(notifications.progress(item, progress), {
        id: toastId,
        timeout: 0,
        closeOnClick: true
      })
    }
  },
  
  notifySuccess({ commit, state }: any, { item, vm }: { item: DownloadItem; response?: any; vm: any }) {
    const toastId = `toast-${item.id}`
    
    if (vm && vm.$snotify) {
      vm.$snotify.html(notifications.success(item), {
        id: toastId,
        timeout: 3000,
        closeOnClick: true
      })
    } else {
      console.log('Download completed for:', item.data?.name)
    }
  },
  
  notifyError({ commit, state }: any, { item, error, vm }: { item: DownloadItem; error: any; vm: any }) {
    const toastId = `toast-${item.id}`
    const errorMsg = error.message || 'Неизвестная ошибка'
    
    if (vm && vm.$snotify) {
      vm.$snotify.html(notifications.error(item, errorMsg), {
        id: toastId,
        timeout: 5000,
        closeOnClick: true
      })
    } else {
      console.error(`Download failed for ${item.data?.name}:`, errorMsg)
    }
  }
}

// Экспорт модуля
export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}